import { useEffect, useMemo, useState } from "react";
import { useAuth0 } from "@auth0/auth0-react";
import * as Sentry from "@sentry/react";
import {
  getStoredGuestSession,
  GuestSession,
} from "../services/guestSessionService";

export type AuthMode = "loading" | "anon" | "guest" | "user";

export interface AuthState {
  mode: AuthMode;
  guest?: {
    sessionId: string;
    expiresAtMs: number;
    quotaBytes: number;
    /**
     * Soft (client-side) view of how much of the quota has been consumed.
     * Populated from the GetUser response once a quota-aware backend ships;
     * the App-shell falls back to 0 in the meantime so the banner / meter
     * render without flashing nonsense numbers.
     */
    usedBytes: number;
  };
}

const readGuest = (): GuestSession | null => getStoredGuestSession();

/**
 * useAuthMode is the single source of truth for "who is this caller" on the
 * frontend. It collapses Auth0 loading + Auth0 isAuthenticated + the guest
 * session token into a 4-state enum the App shell + sidebar can switch on.
 *
 * Phase 2 keeps usedBytes at zero until a guest-aware GetUser lands; later
 * phases will surface real storage_used_bytes via the same shape.
 */
export const useAuthMode = (): AuthState => {
  const { isLoading, isAuthenticated } = useAuth0();
  const [guest, setGuest] = useState<GuestSession | null>(() => readGuest());

  useEffect(() => {
    // Re-read the guest token whenever Auth0 state flips. This catches the
    // post-login window where the Auth0 callback writes the token and the
    // localStorage flag has not yet been cleared.
    setGuest(readGuest());
  }, [isAuthenticated, isLoading]);

  // Storage events let other tabs that just signed in / out propagate state.
  useEffect(() => {
    const handler = (e: StorageEvent) => {
      if (!e.key || e.key.startsWith("ommuse.guest")) {
        setGuest(readGuest());
      }
    };
    window.addEventListener("storage", handler);
    return () => window.removeEventListener("storage", handler);
  }, []);

  const state = useMemo<AuthState>(() => {
    if (isLoading) return { mode: "loading" };
    if (isAuthenticated) return { mode: "user" };
    if (guest) {
      return {
        mode: "guest",
        guest: {
          sessionId: guest.sessionId,
          expiresAtMs: guest.expiresAtMs,
          quotaBytes: guest.quotaBytes,
          usedBytes: 0,
        },
      };
    }
    return { mode: "anon" };
  }, [isLoading, isAuthenticated, guest]);

  // Tag every Sentry event with the resolved auth mode so error queries can
  // slice by user / guest / anon. Cheap setTag — only re-runs on transitions.
  useEffect(() => {
    if (state.mode === "loading") return;
    Sentry.setTag("auth_mode", state.mode);
  }, [state.mode]);

  return state;
};
